export type PortalHelpArticleCategory =
  | 'getting-started'
  | 'tenant-workspaces'
  | 'support'
  | 'maps'
  | 'sales'
  | 'commerce'
  | 'automation'
  | 'settings';

export type PortalHelpSection = {
  id: string;
  title: string;
  paragraphs: string[];
};

export type PortalHelpArticle = {
  articleId: string;
  title: string;
  summary: string;
  category: PortalHelpArticleCategory;
  keywords: string[];
  sections: PortalHelpSection[];
  relatedArticleIds: string[];
  relatedRoutes: string[];
  overlayContexts?: string[];
  metrics?: Array<{ label: string; value: string }>;
  canonicalRoute?: string;
};

export type PortalHelpRoute = {
  kind: 'help';
  articleId: string | null;
};

const OVERLAY_QUERY_PARAM = 'help';

function normalizePath(path: string): string {
  const [pathname = '/'] = path.split('?');
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname || '/';
}

function splitPath(path: string | null | undefined) {
  const value = path || '';
  const [pathname = '', search = ''] = value.split('?');
  return {
    pathname: pathname || '/',
    params: new URLSearchParams(search),
  };
}

export function parsePortalHelpRoute(path: string): PortalHelpRoute | null {
  const normalized = normalizePath(path);
  const segments = normalized.split('/').filter(Boolean);

  if (segments[0] !== 'help') {
    return null;
  }

  if (segments.length === 1) {
    return { kind: 'help', articleId: null };
  }

  if (segments.length === 2) {
    return { kind: 'help', articleId: decodeURIComponent(segments[1]) };
  }

  return null;
}

export function buildPortalHelpRoute(articleId?: string | null) {
  const trimmed = articleId?.trim();
  return trimmed ? `/help/${encodeURIComponent(trimmed)}` : '/help';
}

export function getPortalHelpOverlayArticleId(path: string | null | undefined) {
  const { params } = splitPath(path);
  const articleId = params.get(OVERLAY_QUERY_PARAM)?.trim();
  return articleId || null;
}

export function buildPortalHelpOverlayRoute(articleId: string, path?: string | null) {
  const { pathname, params } = splitPath(
    path || (typeof window === 'undefined' ? '/' : `${window.location.pathname}${window.location.search}`)
  );
  params.set(OVERLAY_QUERY_PARAM, articleId);
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}

export function clearPortalHelpOverlayRoute(path: string | null | undefined) {
  const { pathname, params } = splitPath(path);
  params.delete(OVERLAY_QUERY_PARAM);
  const search = params.toString();
  return search ? `${pathname}?${search}` : pathname;
}

export function resolveContextualPortalHelpArticleId(path: string | null | undefined): string | null {
  const normalized = normalizePath(path || '/');

  if (normalized === '/' || normalized === '/dashboard' || normalized === '/accounts') {
    return 'portal-home';
  }
  if (normalized === '/support') {
    return 'support';
  }
  if (normalized.startsWith('/accounts/')) {
    if (normalized.includes('/services')) {
      return 'account-services';
    }
    if (normalized.includes('/tickets')) {
      return 'support';
    }
    if (normalized.includes('/billing')) {
      return 'billing-payments';
    }
    return 'accounts-overview';
  }
  if (normalized === '/settings') {
    return 'settings';
  }
  if (normalized === '/sales' || normalized.startsWith('/sales/')) {
    if (normalized === '/sales/quotes' || normalized === '/sales/orders') {
      return 'commerce-orders';
    }
    return 'sales-map';
  }
  if (normalized === '/agent/quotes' || normalized.startsWith('/agent/quotes/')) {
    return 'commerce-orders';
  }
  if (normalized.startsWith('/maps/')) {
    return 'maps';
  }
  if (normalized === '/api-gateway/access' || normalized === '/api-gateway/console' || normalized === '/api-access') {
    return 'api-gateway';
  }
  if (normalized.startsWith('/commerce/help/')) {
    return `commerce-${decodeURIComponent(normalized.split('/')[3] || 'catalogs')}`;
  }
  if (normalized === '/commerce/help') {
    return 'commerce-catalogs';
  }
  if (normalized.startsWith('/commerce/catalogs')) {
    return 'commerce-catalogs';
  }
  if (normalized.startsWith('/commerce/publications')) {
    return 'commerce-publications';
  }
  if (normalized.startsWith('/commerce/channels')) {
    return 'commerce-channels';
  }
  if (normalized.startsWith('/commerce/markets')) {
    return 'commerce-markets';
  }
  if (normalized.startsWith('/commerce/eligibility')) {
    return 'commerce-eligibility';
  }
  if (normalized.startsWith('/commerce/entitlements') || normalized.startsWith('/reseller/entitlements')) {
    return 'commerce-reseller_entitlements';
  }
  if (normalized.startsWith('/commerce/pricebooks')) {
    return 'commerce-pricebooks';
  }
  if (normalized.startsWith('/commerce/assignments')) {
    return 'commerce-assignments';
  }
  if (normalized.startsWith('/commerce/commission-plans')) {
    return 'commerce-commission_plans';
  }
  if (normalized.startsWith('/commerce/commission-ledger') || normalized.startsWith('/reseller/commission-ledger')) {
    return 'commerce-commission_ledger';
  }
  if (normalized.startsWith('/commerce/discounts')) {
    return 'commerce-discounts';
  }
  if (
    normalized.startsWith('/commerce/carts') ||
    normalized.startsWith('/reseller/carts') ||
    normalized.startsWith('/commerce/quotes') ||
    normalized.startsWith('/agent/quotes') ||
    normalized.startsWith('/commerce/orders') ||
    normalized.startsWith('/reseller/orders')
  ) {
    return 'commerce-orders';
  }

  return null;
}

export function portalHelpSearchTerms(article: PortalHelpArticle): string[] {
  return [
    article.title,
    article.summary,
    ...article.keywords,
    ...article.sections.map(section => section.title),
    ...article.sections.flatMap(section => section.paragraphs),
  ];
}
